import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  HostBinding,
  input,
  Input,
  OnChanges,
  Output,
  QueryList
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BreakpointService, ModalService } from '@app/_services';
import { map } from 'rxjs';
import { ScrollableContentComponent } from '../../components/scrollableContent';

@Component({
    selector: 'block-content',
    template: '@if (active) {<div class="block__content"><ng-content></ng-content></div>}',
    standalone: false
})

export class BlockContentComponent {
  @Input() tabLabel: string;
  @Input() tabLink: string;
  @Input() tabIcon: string;
  @Input() tabHeading: boolean = false;
  @Input() tabActive: boolean;
  @Input() active: boolean = false;
  @Input() tabVisible: boolean = true;
  @Input() noPaddingTop = false;
  @Input() queryParams: any = {};
  @ContentChildren(forwardRef(() => ScrollableContentComponent), { descendants: true })
  scrollable: QueryList<ScrollableContentComponent>;

  @HostBinding('class') get hostClasses(): string {
    return this.noPaddingTop ? 'no__padding__top' : '';
  }

  constructor(
    private cdr: ChangeDetectorRef,
  ) { }

  hide() {
    this.active = false;
    this.cdr.detectChanges();
  }

  show() {
    this.active = true;
    setTimeout(() => {
        try {
          this.scrollable.forEach((item) => {
            item.checkArrows();
          });
        } catch (err) {
        }
      }, 0);
    this.cdr.detectChanges();
  }
}

@Component({
    selector: 'block-title',
    template: '<ng-content></ng-content>',
    standalone: false
})

export class BlockTitleComponent {
  constructor(
    private cdr: ChangeDetectorRef,
  ) {
  }

  detectChanges() {
    this.cdr.detectChanges();
  }
}

@Component({
    selector: 'block-secondary-title',
    template: '<ng-content></ng-content>',
    standalone: false
})

export class BlockSecondaryTitleComponent {
  constructor(
    private cdr: ChangeDetectorRef,
  ) {
  }

  detectChanges() {
    this.cdr.detectChanges();
  }
}

@Component({
    selector: 'block-secondary-title-subtext',
    template: '<ng-content></ng-content>',
    standalone: false
})

export class BlockSecondaryTitleSubtextComponent {
}

@Component({
    selector: 'block-sub-title',
    template: '<ng-content></ng-content>',
    standalone: false
})

export class BlockSubTitleComponent {
  constructor(
    private cdr: ChangeDetectorRef,
  ) {
  }

  detectChanges() {
    this.cdr.detectChanges();
  }
}

@Component({
    selector: 'block-tabs',
    template: '<ng-content></ng-content>',
    standalone: false
})

export class BlockTabsComponent {
}

@Component({
    selector: 'block',
    templateUrl: './block.template.html',
    styleUrls: ['./block.styles.scss'],
    standalone: false
})

export class BlockComponent implements AfterContentInit, OnChanges {

  @Input() loading: boolean = false;

  public viewTabs: QueryList<BlockContentComponent> | Object[];
  public currentViewTabs: number = 0;
  public isMobile = toSignal(
		this.breakpointService.isMobile$.pipe(map((value) => value?.matches))
	);
  public activeTab: string = '';
  labeledTabs: number = 0;
  hasTitle: boolean = false;
  hasSecondaryTitle: boolean = false;
  @ContentChildren(forwardRef(() => BlockContentComponent))
  tabs: QueryList<BlockContentComponent>;
  @ContentChildren(forwardRef(() => BlockTitleComponent))
  titleComponent: QueryList<BlockTitleComponent>;
  @ContentChildren(forwardRef(() => BlockSecondaryTitleComponent))
  secondaryTitleComponent: QueryList<BlockSecondaryTitleComponent>;
  @Input() theme: string = 'blue';
  @Input() titleBorder: boolean = true;
  fullWidthTitle = input(false);
  @Input() tabStyle: string = 'default';
  @Output() tabChange = new EventEmitter<any>();

  constructor(
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private breakpointService: BreakpointService,
    private router: Router,
  ) {}

  @HostBinding('class') get hostClasses(): string {
    return `block--${this.theme}`;
  }

  changeTab(title: string) {
    this.activeTab = title;
  }

  selectTab(tab) {
    this.tabs.toArray().forEach(tab => tab.hide());
    tab.show();
    this.activeTab = tab.tabLabel;
    this.tabChange.emit(tab.tabLabel);
    this.cdr.detectChanges();
  }

  countLabels() {
    this.labeledTabs = this.tabs.filter((tab) => {
      return !!tab.tabLabel;
    }).length;
  }

  checkTitle() {
    try {
      this.hasTitle = this.titleComponent.toArray().length > 0;
    } catch (err) {
    }
  }

  checkSecondaryTitle() {
    try {
      this.hasSecondaryTitle = this.secondaryTitleComponent.toArray().length > 0;
    } catch (err) {
    }
  }

  public navigateTabs(navigation: number) {
    this.currentViewTabs = this.currentViewTabs + navigation;
  }

  public constructViewTabs() {
    const viewTabs: Object[] = [];
    const tmpTabs = this.tabs.toArray().filter((item) => {
      if (item.tabVisible) {
        return item;
      }
    });
    for (let i = 3; i <= tmpTabs.length; i += 3) {
      viewTabs.push(tmpTabs.slice(i - 3, i));
    }
    if (tmpTabs.length % 3) {
      viewTabs.push(tmpTabs.slice(tmpTabs.length - 3, tmpTabs.length));
    }
    this.viewTabs = viewTabs;
  }

  navigateTo(tabLink, params = {}) {
    const flattenedParams = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
    const paramsToAdd = flattenedParams?.length ? `?${flattenedParams}` : '';
    this.router.navigateByUrl(`${tabLink}${paramsToAdd}`);
  }

  ngAfterContentInit() {
    this.contentInit();
  }

  ngOnChanges() {
    this.cdr.detectChanges();
    this.contentInit();
  }

  private contentInit() {
    try {
      let activeTabs;
      this.tabs.forEach((item) => {
        if (item.tabActive) {
          activeTabs = [item];
        }
      });

      if (!activeTabs) {
        activeTabs = this.tabs.filter(tab => tab.active);
      } else {
        activeTabs[0].active = true;
      }
      if (activeTabs.length === 0) {
        this.selectTab(this.tabs.first || this.tabs[0]);
      } else {
        this.activeTab = activeTabs[0].tabLabel;
        activeTabs[0]?.cdr?.detectChanges();
      }
      if (this.tabs.length > 3 && this.isMobile()) {
        this.constructViewTabs();
      } else {
        this.viewTabs = [this.tabs];
      }
      this.countLabels();
    } catch (err) {
    }

    this.checkTitle();
    this.checkSecondaryTitle();
  }

}
